// src/ReplenishReport.js
import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "./api";
const safeText = (v) => (v === null || v === undefined ? "" : String(v));
export default function ReplenishReport({ onBack }) {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  /* -------------------- helpers -------------------- */
  const showMsg = (m) => {
    setMsg(m);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 3500);
  };

  const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  };

  const badgeForAction = (a) => {
    const x = String(a || "").toUpperCase();
    if (x === "SALE") return { ...ui.badge, ...ui.badgeRed };
    if (x === "ADD") return { ...ui.badge, ...ui.badgeBlue };
    if (x === "REPLENISH") return { ...ui.badge, ...ui.badgeGreen };
    return { ...ui.badge, ...ui.badgeGray };
  };

  /* -------------------- load report -------------------- */
  const load = async () => {
    setLoading(true);
    try {
      const params = {
        q: q || undefined,
        action: action || undefined,
        from: from || undefined,
        to: to || undefined,
      };

      const res = await api.get("/server.php/stock-report", { params });

      if (res.data?.success) {
        const list = Array.isArray(res.data.rows) ? res.data.rows : [];
        setRows(list);
        if (list.length === 0) showMsg("No records found");
      } else {
        setRows([]);
        showMsg(res.data?.message || "Failed to load report");
      }
    } catch (e) {
      console.error("Stock report error:", e);
      showMsg(e?.response?.data?.message || e?.response?.data?.error || "Server error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  /* Auto reload when filters change */
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, action, from, to]);

  /* -------------------- totals -------------------- */
  const totals = useMemo(() => {
    let added = 0;
    let sold = 0;

    rows.forEach((r) => {
      const c = Number(r.qty_change ?? 0);
      if (c > 0) added += c;
      if (c < 0) sold += Math.abs(c);
    });

    return { added, sold };
  }, [rows]);

  /* -------------------- render -------------------- */
  return (
    <div style={ui.page}>
      <div style={ui.shell}>
        {/* Header */}
        <div style={ui.headerRow}>
          <div>
            <div style={ui.h1}>Replenishment & Stock Report</div>
            <div style={ui.sub}>
              API: <span style={ui.mono}>{API_BASE}</span>
            </div>
          </div>

          <div style={ui.headerActions}>
            <button onClick={load} style={ui.btn} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button onClick={onBack} style={ui.btnGhost}>
              Back
            </button>
          </div>
        </div>

        {/* Toast / Message */}
        {msg ? <div style={ui.toast}>{msg}</div> : null}

        {/* Filters Card */}
        <div style={ui.card}>
          <div style={ui.cardTitle}>Filters</div>
          <div style={ui.filtersGrid}>
            <div style={ui.field}>
              <div style={ui.label}>Item name</div>
              <input
                placeholder="e.g., sugar"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={ui.input}
              />
            </div>

            <div style={ui.field}>
              <div style={ui.label}>Action</div>
              <select value={action} onChange={(e) => setAction(e.target.value)} style={ui.input}>
                <option value="">All</option>
                <option value="SALE">SALE</option>
                <option value="ADD">ADD</option>
                <option value="REPLENISH">REPLENISH</option>
              </select>
            </div>

            <div style={ui.field}>
              <div style={ui.label}>From</div>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={ui.input} />
            </div>

            <div style={ui.field}>
              <div style={ui.label}>To</div>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={ui.input} />
            </div>

            <div style={ui.fieldActions}>
              <button onClick={load} style={ui.primaryBtn} disabled={loading}>
                {loading ? "Loading…" : "Search"}
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div style={ui.kpis}>
          <div style={ui.kpi}>
            <div style={ui.kpiLabel}>Total Added</div>
            <div style={ui.kpiValue}>{totals.added}</div>
          </div>
          <div style={ui.kpi}>
            <div style={ui.kpiLabel}>Total Sold</div>
            <div style={ui.kpiValue}>{totals.sold}</div>
          </div>
          <div style={ui.kpi}>
            <div style={ui.kpiLabel}>Rows</div>
            <div style={ui.kpiValue}>{rows.length}</div>
          </div>
        </div>

        {/* Table Card */}
        <div style={ui.tableCard}>
          <div style={ui.tableTop}>
            <div style={ui.tableTitle}>Movements</div>
            <div style={ui.tableHint}>
              Tip: use <b>Action</b> + date range to narrow results.
            </div>
          </div>

          <div style={ui.tableWrap}>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={{ ...ui.th, width: 170 }}>Date</th>
                  <th style={{ ...ui.th, width: 320 }}>Item</th>
                  <th style={{ ...ui.thRight, width: 90 }}>Before</th>
                  <th style={{ ...ui.thRight, width: 90 }}>Change</th>
                  <th style={{ ...ui.thRight, width: 90 }}>After</th>
                  <th style={{ ...ui.th, width: 120 }}>Action</th>
                  <th style={{ ...ui.th, width: 140 }}>Ref</th>
                  <th style={{ ...ui.th, width: 160 }}>User</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={ui.tdEmpty}>
                      {loading ? "Loading…" : "No records found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const change = Number(r.qty_change ?? 0);
                    const changeStyle =
                      change < 0 ? ui.neg : change > 0 ? ui.pos : ui.neu;

                    return (
                      <tr key={r.id} style={idx % 2 ? ui.trAlt : undefined}>
                        <td style={ui.td}>{fmtDate(r.created_at)}</td>
                        <td style={ui.tdItem} title={safeText(r.itemname)}>
                          {safeText(r.itemname)}
                        </td>
                        <td style={ui.tdRight}>{safeText(r.qty_before)}</td>
                        <td style={{ ...ui.tdRight, ...changeStyle }}>{safeText(r.qty_change)}</td>
                        <td style={ui.tdRight}>{safeText(r.qty_after)}</td>
                        <td style={ui.td}>
                          <span style={badgeForAction(r.action)}>{safeText(r.action) || "—"}</span>
                        </td>
                        <td style={ui.td}>{safeText(r.reference) || "—"}</td>
                        <td style={ui.td}>{safeText(r.username) || "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer action */}
        <div style={ui.footer}>
          <button onClick={onBack} style={ui.btnGhost}>
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- UI styles -------------------- */
const ui = {
  page: {
    background: "#f6f7fb",
    minHeight: "100vh",
    padding: 18,
  },
  shell: {
    maxWidth: 1180,
    margin: "0 auto",
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 14,
  },
  h1: {
    fontSize: 42,
    fontWeight: 950,
    letterSpacing: -1,
    lineHeight: 1.05,
  },
  sub: {
    marginTop: 6,
    color: "#6b7280",
    fontWeight: 800,
    fontSize: 13,
  },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    background: "#eef2ff",
    border: "1px solid #dbeafe",
    color: "#1f2937",
    padding: "2px 8px",
    borderRadius: 999,
  },

  headerActions: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },

  toast: {
    background: "#fff",
    border: "1px solid #eaecf5",
    borderRadius: 16,
    padding: "10px 12px",
    fontWeight: 850,
    color: "#111827",
    boxShadow: "0 10px 24px rgba(16,24,40,0.06)",
    marginBottom: 12,
    textAlign: "center",
  },

  card: {
    background: "#fff",
    border: "1px solid #eaecf5",
    borderRadius: 20,
    padding: 16,
    boxShadow: "0 10px 24px rgba(16,24,40,0.06)",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 950,
    color: "#111827",
    marginBottom: 12,
  },

  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "1.2fr 0.8fr 0.7fr 0.7fr auto",
    gap: 12,
    alignItems: "end",
  },
  field: { display: "flex", flexDirection: "column", gap: 8 },
  fieldActions: { display: "flex", justifyContent: "flex-end" },

  label: { fontSize: 13, fontWeight: 900, color: "#111827" },
  input: {
    border: "1px solid #d8dbe6",
    borderRadius: 16,
    padding: "12px 14px",
    outline: "none",
    fontWeight: 800,
    background: "#fff",
  },

  btn: {
    border: "1px solid #d8dbe6",
    background: "#fff",
    padding: "10px 14px",
    borderRadius: 16,
    cursor: "pointer",
    fontWeight: 900,
  },
  btnGhost: {
    border: "1px solid #d8dbe6",
    background: "transparent",
    padding: "10px 14px",
    borderRadius: 16,
    cursor: "pointer",
    fontWeight: 900,
  },
  primaryBtn: {
    border: "1px solid #2f6fed",
    background: "#2f6fed",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 16,
    cursor: "pointer",
    fontWeight: 950,
    boxShadow: "0 10px 18px rgba(47,111,237,0.22)",
    whiteSpace: "nowrap",
  },

  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginTop: 12,
    marginBottom: 12,
  },
  kpi: {
    background: "#fff",
    border: "1px solid #eaecf5",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 10px 24px rgba(16,24,40,0.06)",
  },
  kpiLabel: { color: "#6b7280", fontWeight: 900, fontSize: 12 },
  kpiValue: { fontWeight: 950, fontSize: 22, marginTop: 6 },

  tableCard: {
    marginTop: 6,
    background: "#fff",
    border: "1px solid #eaecf5",
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 10px 24px rgba(16,24,40,0.06)",
  },
  tableTop: {
    padding: 14,
    borderBottom: "1px solid #eaecf5",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 10,
  },
  tableTitle: { fontWeight: 950, fontSize: 16, color: "#111827" },
  tableHint: { color: "#6b7280", fontWeight: 800, fontSize: 12 },

  tableWrap: {
    overflowX: "auto",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },

  th: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    padding: "12px 12px",
    background: "#f6f7fb",
    borderBottom: "1px solid #eaecf5",
    fontWeight: 950,
    color: "#111827",
    fontSize: 13,
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  thRight: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    padding: "12px 12px",
    background: "#f6f7fb",
    borderBottom: "1px solid #eaecf5",
    fontWeight: 950,
    color: "#111827",
    fontSize: 13,
    textAlign: "right",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "12px 12px",
    borderBottom: "1px solid #f0f1f7",
    fontWeight: 800,
    color: "#111827",
    whiteSpace: "nowrap",
  },
  tdRight: {
    padding: "12px 12px",
    borderBottom: "1px solid #f0f1f7",
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    textAlign: "right",
  },
  tdItem: {
    padding: "12px 12px",
    borderBottom: "1px solid #f0f1f7",
    fontWeight: 850,
    color: "#111827",
    maxWidth: 520,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tdEmpty: {
    padding: 18,
    textAlign: "center",
    color: "#6b7280",
    fontWeight: 900,
  },

  trAlt: { background: "#fbfcff" },

  pos: { color: "#0f766e" }, // teal-ish
  neg: { color: "#b91c1c" }, // red-ish
  neu: { color: "#374151" },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontWeight: 950,
    fontSize: 12,
    border: "1px solid transparent",
  },
  badgeRed: { background: "#fee2e2", color: "#991b1b", borderColor: "#fecaca" },
  badgeBlue: { background: "#dbeafe", color: "#1d4ed8", borderColor: "#bfdbfe" },
  badgeGreen: { background: "#dcfce7", color: "#166534", borderColor: "#bbf7d0" },
  badgeGray: { background: "#f3f4f6", color: "#374151", borderColor: "#e5e7eb" },

  footer: {
    marginTop: 14,
    display: "flex",
    justifyContent: "center",
  },
};